import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Form, Formik } from 'formik';
import useAsync from '@/hooks/useAsync';
import { ChangeEvent, FC } from 'react';
import { NotificationModal } from '@/components/Modal';
import { useModal } from '@ajorquera/libs/react';

export interface Values {
  action?: 'cannot-be-performed' | 'come-to-clinic' | 'retake-test' | 'ok';
  notes?: string;
}

interface Props {
  onSubmit: (values: Values) => any;
  values: Values;
  disabled?: boolean;
}

const TestReviewForm: FC<Props> = ({ onSubmit, values, disabled }) => {
  const { loading, execute } = useAsync(onSubmit);
  const { open } = useModal(NotificationModal, { title: 'Notification' });

  const onChangeAction = (e: ChangeEvent<HTMLSelectElement>, cb: (e: ChangeEvent<HTMLSelectElement>) => void) => {
    cb(e);
    const action = e.target.value as Values['action'];
    let message = '';
    if (action === 'ok') {
      message = 'No further action required. Surgery to proceed as scheduled.';
    } else if (action === 'come-to-clinic') {
      message =
        "An in person exam should be performed. An email will be sent notifying the facility's schedulers. Your notes will be included in the message to the schedulers.";
    } else if (action === 'retake-test') {
      message =
        'A notification will be sent to the patient to retake the exam. The notes will be included in the notification.';
    } else if (action === 'cannot-be-performed') {
      message =
        "Based on your assessment this patient should be scheduled at an alternate facility (eg, hospital). A notification with your recommendation will be sent to the facility's schedulers. The notes will be included.";
    }

    open({ children: message });
  };

  return (
    <Formik initialValues={{ notes: values?.notes, action: '' }} onSubmit={execute}>
      {({ getFieldProps, values }) => (
        <Form>
          <Box display={'flex'} gap={3} my={2}>
            <FormControl sx={{ flexGrow: 1 }}>
              <InputLabel id="demo-simple-select-label">Select Action</InputLabel>
              <Select
                label="Select Action"
                {...getFieldProps('action')}
                onChange={(e) => onChangeAction(e, getFieldProps('action').onChange)}
              >
                <MenuItem value="ok">Ok to Proceed</MenuItem>
                <MenuItem value="cannot-be-performed">Alternate Facility Recommended</MenuItem>
                <MenuItem value="retake-test">Request Patient Retake Exam</MenuItem>
                <MenuItem value="come-to-clinic">Recommend In Person Airway Exam</MenuItem>
              </Select>
            </FormControl>

            <Button
              disabled={disabled || !values.action}
              loading={loading}
              size="large"
              variant="contained"
              type="submit"
            >
              Submit
            </Button>
          </Box>
          <TextField
            label="Notes"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="Notes to Facility Schedulers or Patient (If choosing “Request Patient Retake Exam”)"
            {...getFieldProps('notes')}
          />
        </Form>
      )}
    </Formik>
  );
};

export default TestReviewForm;
